import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Login from './pages/Login';
import TeacherDashboard from './pages/TeacherDashboard';
import TutorDashboard from './pages/TutorDashboard';
import ClassesPage from './pages/ClassesPage';
import AvanceProgramaticoPage from './pages/AvanceProgramaticoPage';
import ClassView from './pages/ClassView';
import AdminDashboard from './pages/AdminDashboard';
import AdminSettingsPage from './pages/AdminSettingsPage';
import ControlEscolarDashboard from './pages/ControlEscolarDashboard';
import PartialDatesPage from './pages/PartialDatesPage';
import OrientadorDashboard from './pages/OrientadorDashboard';
import JefeDocentesDashboard from './pages/JefeDocentesDashboard';
import JefeTutoresDashboard from './pages/JefeTutoresDashboard';
import GrupoTutoradoPage from './pages/GrupoTutoradoPage';
import GrupoTutoradoReportPage from './pages/GrupoTutoradoReportPage';
import TIAccessPage from './pages/TIAccessPage';
import TeacherAccessPage from './pages/TeacherAccessPage';
import TeacherSettingsPage from './pages/TeacherSettingsPage';
import ChangePasswordModal from './components/ChangePasswordModal';
import MaintenanceScreen from './components/MaintenanceScreen';
import TutorInactivityWatcher from './components/TutorInactivityWatcher';
import AlertasPage from './pages/AlertasPage';
import OrientadorClasesPage from './pages/OrientadorClasesPage';
import HistorialConductaPage from './pages/HistorialConductaPage';
import ContactoPadresPage from './pages/ContactoPadresPage';
import ActualizarContactoPage from './pages/ActualizarContactoPage';
import ResumenIncumplimientoPage from './pages/ResumenIncumplimientoPage';
import PrefecturaDashboard from './pages/PrefecturaDashboard';
import SubdirectorDashboard from './pages/SubdirectorDashboard';
import HorariosPage from './pages/HorariosPage';
import { SettingsProvider } from './contexts/SettingsContext';
import { addBytes, estimateSize, resetDataMeter } from './utils/dataMeter';
import { Toaster } from 'sonner';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [maintenance, setMaintenance] = useState(false);

  useEffect(() => {
    // Global 401 handler con RENOVACION AUTOMATICA de sesion.
    // El token de acceso ahora es corto (12h). Cuando expira, en vez de cerrar
    // sesion de inmediato intentamos renovarlo silenciosamente con el
    // refresh_token (14 dias). Si la renovacion funciona, reintentamos la
    // peticion original y el usuario ni se entera. Si el refresh tambien expiro
    // o no existe (sesion vieja/abandonada), recien ahi cerramos sesion limpio.
    let refreshPromise = null;
    const API = process.env.REACT_APP_BACKEND_URL;

    const doRefresh = async () => {
      const rt = localStorage.getItem('refresh_token');
      if (!rt) throw new Error('no-refresh-token');
      const resp = await axios.post(
        `${API}/api/auth/refresh`,
        { refresh_token: rt },
        { _skipAuthRefresh: true }
      );
      const newToken = resp.data.token;
      localStorage.setItem('token', newToken);
      return newToken;
    };

    const forceLogout = () => {
      if (localStorage.getItem('token')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
      }
    };

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const config = error?.config || {};
        const onLoginPage = window.location.pathname === '/login';

        if (status === 401 && !config._skipAuthRefresh && !config._retried && !onLoginPage) {
          if (localStorage.getItem('refresh_token')) {
            try {
              if (!refreshPromise) {
                refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
              }
              const newToken = await refreshPromise;
              config._retried = true;
              config.headers = { ...(config.headers || {}), Authorization: `Bearer ${newToken}` };
              return axios(config);
            } catch (e) {
              forceLogout();
              return Promise.reject(error);
            }
          }
          // Sin refresh token (sesion vieja previa a esta mejora) -> logout.
          forceLogout();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // Session data meter: accumulate approximate bytes sent/received via axios so
  // the teacher can see how much data the app uses this session. Estimates only.
  useEffect(() => {
    const reqId = axios.interceptors.request.use((config) => {
      try { config.metadata = { reqBytes: estimateSize(config.data, config.headers) }; } catch (e) { /* ignore */ }
      return config;
    });
    const resId = axios.interceptors.response.use(
      (response) => {
        try {
          const reqBytes = response.config?.metadata?.reqBytes || 0;
          const resBytes = estimateSize(response.data, response.headers);
          addBytes(reqBytes + resBytes);
        } catch (e) { /* ignore */ }
        return response;
      },
      (error) => {
        try {
          const reqBytes = error.config?.metadata?.reqBytes || 0;
          const resBytes = error.response ? estimateSize(error.response.data, error.response.headers) : 0;
          addBytes(reqBytes + resBytes);
        } catch (e) { /* ignore */ }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []);

  // Validate the stored session against the backend. Forces the login screen
  // when there is no token (e.g. after logout, including iOS "Add to Home Screen"
  // resumes that restore a frozen logged-in page) or when the token is invalid.
  const validateSession = useCallback(async () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    if (!token) {
      // No session: ensure we are NOT showing a protected/frozen page.
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
      return;
    }
    try {
      await axios.get(`${API}/api/users/me`, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      // 401 -> invalid/expired session: clear and go to login.
      // (The global 401 interceptor also handles this, but we redirect here
      //  explicitly so the login screen always shows on resume.)
      if (err?.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refresh_token');
        setUser(null);
        if (window.location.pathname !== '/login') {
          window.location.replace('/login');
        }
      }
      // Network errors are ignored so a flaky connection never logs the user out.
    }
  }, []);

  useEffect(() => {
    // Re-validate the session whenever the app is reopened/resumed. iOS standalone
    // web clips often restore a frozen page (sometimes WITHOUT setting
    // pageshow.persisted), so we cover pageshow + visibilitychange + focus.
    const onResume = () => { validateSession(); };
    const onPageShow = (e) => {
      // bfcache restore with no token -> force a reload to drop the stale page.
      if (e.persisted && !localStorage.getItem('token')) {
        window.location.replace('/login');
        return;
      }
      validateSession();
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') validateSession(); };

    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onResume);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onResume);
    };
  }, [validateSession]);

  useEffect(() => {
    // Check maintenance mode
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/system/status`).then(r => r.json()).then(d => {
      setMaintenance(d.maintenance || false);
    }).catch(() => {});

    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      // Check if user needs to change password
      if (parsedUser.must_change_password) {
        setShowPasswordModal(true);
      }
      // Validate the token with the backend on load; logs out if invalid.
      validateSession();
    }
    setLoading(false);
  }, []);

  // Heartbeat for teacher presence tracking (every 60s)
  useEffect(() => {
    if (!user || user.role !== 'teacher') return;
    const API = process.env.REACT_APP_BACKEND_URL;
    const sendHeartbeat = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      fetch(`${API}/api/heartbeat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    };
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogin = (token, userData, refreshToken) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    resetDataMeter();
    setUser(userData);
    // Check if new user needs to change password
    if (userData.must_change_password) {
      setShowPasswordModal(true);
    }
  };

  const handlePasswordChanged = () => {
    // Update user state
    const updatedUser = { ...user, must_change_password: false };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refresh_token');
    setUser(null);
    setShowPasswordModal(false);
    // Hard redirect (full reload) so the in-memory React state is fully cleared
    // and the browser back button / bfcache cannot restore a logged-in page.
    // replace() removes the current (protected) entry from history.
    window.location.replace('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-lg text-slate-600">Cargando...</div>
      </div>
    );
  }

  // Show maintenance screen for any non-admin (or unauthenticated) user.
  // Exception: /login is always accessible. The backend rejects non-admin
  // logins with 503 during maintenance, so security is preserved.
  // No visible link to /login is shown from the maintenance screen.
  if (maintenance && (!user || user.role !== 'admin') && window.location.pathname !== '/login') {
    const logoSrc = `${process.env.REACT_APP_BACKEND_URL}/api/app-icon.png`;
    return <MaintenanceScreen logoSrc={logoSrc} />;
  }

  const getDashboardRoute = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'teacher':
        return '/classes';
      case 'tutor':
        return '/dashboard/tutor';
      case 'admin':
        return '/dashboard/admin';
      case 'control_escolar':
        return '/dashboard/control-escolar';
      case 'orientador':
        return '/dashboard/orientador';
      case 'jefe_docentes':
        return '/dashboard/jefe-docentes';
      case 'jefe_tutores':
        return '/dashboard/jefe-tutores';
      case 'prefectura':
        return '/dashboard/prefectura';
      case 'subdirector':
        return '/dashboard/subdirector';
      default:
        return '/login';
    }
  };

  return (
    <div className="App">
      <SettingsProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={!user ? <Login onLogin={handleLogin} /> : <Navigate to={getDashboardRoute()} />}
            />
            <Route
              path="/dashboard/admin"
              element={user && user.role === 'admin' ? <AdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route path="/admin/groups" element={<Navigate to="/dashboard/admin" replace />} />
            <Route
              path="/admin/settings"
              element={user && user.role === 'admin' ? <AdminSettingsPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/dashboard/control-escolar"
              element={user && user.role === 'control_escolar' ? <ControlEscolarDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/control-escolar/fechas-parciales"
              element={user && user.role === 'control_escolar' ? <PartialDatesPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/dashboard/orientador"
              element={user && user.role === 'orientador' ? <OrientadorDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/dashboard/jefe-docentes"
              element={user && user.role === 'jefe_docentes' ? <JefeDocentesDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/dashboard/jefe-tutores"
              element={user && user.role === 'jefe_tutores' ? <JefeTutoresDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/teacher/grupo-tutorado"
              element={user && user.role === 'teacher' ? <GrupoTutoradoPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/teacher/grupo-tutorado/reporte"
              element={user && user.role === 'teacher' ? <GrupoTutoradoReportPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/ti-access"
              element={user && user.role === 'admin' ? <TIAccessPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/ti-access-jt"
              element={user && user.role === 'jefe_tutores' ? <TIAccessPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/teacher-access"
              element={user && (user.role === 'admin' || user.role === 'jefe_docentes') ? <TeacherAccessPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/change-password"
              element={user ? <TeacherSettingsPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/alertas"
              element={user && ['control_escolar', 'orientador', 'jefe_tutores'].includes(user.role) ? <AlertasPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/orientador/clases"
              element={user && user.role === 'orientador' ? <OrientadorClasesPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            
            />
            <Route
              path="/historial-conducta"
              element={user && user.role === 'orientador' ? <HistorialConductaPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/orientador/contacto-padres"
              element={user && user.role === 'orientador' ? <ContactoPadresPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/control-escolar/contacto-padres"
              element={user && user.role === 'control_escolar' ? <ContactoPadresPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/tutor/actualizar-contacto"
              element={user && user.role === 'tutor' ? <ActualizarContactoPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/tutor/resumen-incumplimiento"
              element={user && user.role === 'tutor' ? <ResumenIncumplimientoPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/dashboard/prefectura"
              element={user && user.role === 'prefectura' ? <PrefecturaDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/dashboard/subdirector"
              element={user && user.role === 'subdirector' ? <SubdirectorDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/horarios"
              element={user && ['prefectura', 'jefe_docentes', 'admin'].includes(user.role) ? (
                <div className="min-h-screen bg-slate-50" data-testid="horarios-standalone">
                  <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
                    <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
                      <div>
                        <h1 className="text-base font-semibold text-slate-900">Cumplimiento de horarios</h1>
                        <p className="text-xs text-slate-500">{user.role === 'prefectura' ? 'Marca el estatus de cada clase del día' : 'Sube y consulta los horarios por grupo'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => window.history.back()} className="text-sm px-3 h-9 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="horarios-back">Volver</button>
                        <button onClick={handleLogout} className="text-sm px-3 h-9 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="horarios-logout">Salir</button>
                      </div>
                    </div>
                  </header>
                  <main className="py-4"><HorariosPage user={user} /></main>
                </div>
              ) : <Navigate to="/login" />}
            />
            <Route
              path="/dashboard/teacher"
              element={user && user.role === 'teacher' ? <TeacherDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
          <Route
            path="/dashboard/tutor"
            element={user && user.role === 'tutor' ? <TutorDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/class/:classId"
            element={user ? <ClassView user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/tutor/class/:classId"
            element={user && user.role === 'tutor' ? <ClassView user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/classes"
            element={user && user.role === 'teacher' ? <ClassesPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/class/:classId/avance-programatico"
            element={user && user.role === 'teacher' ? <AvanceProgramaticoPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/teacher/settings"
            element={user && user.role === 'teacher' ? <TeacherSettingsPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route path="/" element={<Navigate to={user ? getDashboardRoute() : '/login'} />} />
        </Routes>
        
        {/* Password change modal */}
        <ChangePasswordModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          onPasswordChanged={handlePasswordChanged}
        />

        {/* Auto-logout after 15 min inactivity, ONLY for tutor (padre de familia) */}
        {user && user.role === 'tutor' && (
          <TutorInactivityWatcher onLogout={handleLogout} />
        )}

        <Toaster position="top-right" richColors />
      </BrowserRouter>
      </SettingsProvider>
    </div>
  );
}

export default App;
